import { getField, updateField } from 'vuex-map-fields';
import { WEB_BASE_PATH } from '@/api/path';
import {
  updateOpportunityOrderInfo,
  updateOppoIDandTransactionID,
  updateRecurringDonation,
  getSingleDonationAmount,
  getRecurringDonationAmount,
} from '@/api';
import {
  createLinePayTransaction,
  confirmLinePayTransaction,
} from '@/api/LINEPay';
import { getGATracking } from '@/helpers/ga';
import { getMetaTracking } from '@/helpers/meta';

export default {
  namespaced: true,

  state: {
    transactionId: '', // 交易ID (LINE 提供)
    orderId: '', // 訂單編號(LINE需要）：有可能是 單筆機會ID or 定期定額編號 (SF產生）
    oppoId: '', // 機會ID (SF提供)，不論定期或單筆都會有
    amount: '', // 捐款金額
    paymentUrl: { // 捐款連結
      web: '',
      app: '',
    },
    result: '',
    errorMessage: '',
  },

  mutations: {
    updateField,

    updateLinePayOrderid(state, orderId) {
      state.orderId = orderId;
    },

    updateTransaction(state, { transactionId, paymentUrl }) {
      state.transactionId = transactionId;
      state.paymentUrl.web = paymentUrl.web;
      state.paymentUrl.app = paymentUrl.app;
    },

    updateAmountAndOppoId(state, { amount, oppoId }) {
      state.amount = amount;
      state.oppoId = oppoId;
    },

    updateRecurringDonationAmount(state, { amount, oppoId }) {
      state.amount = amount;
      state.oppoId = oppoId;
    },

    updateResult(state, result) {
      state.result = result;
    },
  },

  getters: {
    getField,
    lineDonationResult(state) {
      const fields = [];

      fields.push({
        name: '捐款編號',
        value: state.oppoId,
      });

      if(state.amount) {
        fields.push({
          name: '付款金額',
          value: state.amount,
        });
      }

      fields.push({
        name: '付款方式',
        value: 'LINE Pay',
      });

      fields.push({
        name: '捐款結果',
        value: state.result ? state.result : '捐款成功',
      });

      return fields;
    },
  },

  actions: {
    async getPaymentUrl({ state, rootState, dispatch }) {
      try {
        await dispatch('createLinePayTransaction');
        if(rootState.donation.donationPeriod === '單次捐款') {
          await dispatch('updateOppoIDandTransactionID');
        }
        return state.paymentUrl;
      } catch(e) {
        console.error('Error: Cant get Line Payment URL');
        alert('Error: Cant get Line Payment URL');
      }
    },

    async createLinePayTransaction({ state, rootState, commit }) {
      try {
        const confirmUrl = rootState.donation.donationPeriod == '定期定額' ? `${ WEB_BASE_PATH }/period/result` : `${ WEB_BASE_PATH }/single/result`;
        const payload = {
          amount: rootState.donation.amount,
          productName: rootState.donation.donationPeriod == '定期定額' ? '定期定額' : '單次捐款',
          orderId: state.orderId,
          currency: 'TWD',
          confirmUrl: confirmUrl,
          cancelUrl: confirmUrl,
          donationPeriod: rootState.donation.donationPeriod == '定期定額' ? 'period' : 'single',
        };
        const result = await createLinePayTransaction(payload);
        // 以下這行是必要的，因為在JSON.parse後LINE傳送的transactionId是19位數，因此會有overflow問題，必須先當成字串擷取
        const index = result.data.indexOf('transactionId');
        // 請注意不要直接寫成const transactionId = result.data.info.transactionId;
        const transactionId = result.data.slice(index + 15, index + 34);
        const paymentUrl = JSON.parse(result.data).info.paymentUrl;

        commit('updateTransaction', { transactionId, paymentUrl });
      } catch(err) {
        console.error('Error: Cant create LINE Transaction', err);
        alert('Error: Cant create LINE Transaction' + err + '\n (捐款失敗，請將此錯誤訊息進行截圖，並將截圖寄信至 fin@teach4taiwan.org，將會由專人協助處理，謝謝)');
      }
    },

    async updateOppoIDandTransactionID({ state }) {
      const payload = {
        oppoId: state.orderId, // 單筆捐款的 orderId 是機會ID
        transactionId: state.transactionId,
      };
      const resp = await updateOppoIDandTransactionID(payload);
      return resp;
    },

    async finishLinePaySingleTransaction({ dispatch, commit }) {
      try {
        await dispatch('getSingleDonationAmount');
        const result = await dispatch('confirmTransaction');
        await dispatch('updateOpportunityOrderInfo', result);
        commit('donation/submittingSuccess', null, { root: true });
      } catch(e) {
        alert(e);
      }
    },

    async finishLinePayPeriodTransaction({ dispatch, commit }) {
      try {
        await dispatch('getRecurringDonationAmount');
        const result = await dispatch('confirmTransaction');
        await dispatch('updateRecurringDonation', result);
        commit('donation/submittingSuccess', null, { root: true });
      } catch(e) {
        alert(e);
      }
    },

    async getSingleDonationAmount({ state, commit }) { // for single donation
      const payload = {
        transactionId: state.transactionId,
      };
      const resp = await getSingleDonationAmount(payload);
      const amount = resp.data['Amount'];
      const oppoId = resp.data['OppoId'];
      if(amount && oppoId) {
        commit('updateAmountAndOppoId', { amount, oppoId });
      }
    },

    async getRecurringDonationAmount({ state, commit }) { // for recurring donation
      const payload = {
        recurDonationOrderId: state.orderId,
      };
      try {
        const resp = await getRecurringDonationAmount(payload);
        const amount = resp.data['amount'];
        const oppoId = resp.data['parentRecurringDonationID'];
        if(amount && oppoId) {
          commit('updateRecurringDonationAmount', { amount, oppoId });
        }
      } catch(err) {
        alert.error(err);
      }
    },

    async confirmTransaction({ state, rootState }) {
      try {
        const {
          amount,
          transactionId,
        } = state;
        if(!amount || !transactionId) throw new Error('parameter error.');

        const payload = {
          amount: state.amount,
          transactionId: state.transactionId,
          orderId: state.orderId,
          tracking: {
            ...getGATracking(), // 讓後端 GA4 MP 事件歸到同一位使用者，避免重複交易
            ...getMetaTracking(), // fbc/fbp：讓後端 CAPI 事件能歸因到廣告點擊（提高 fbc 涵蓋率）
            email: rootState.donation.contact.email, // 後端會 SHA-256 雜湊後供 Meta 進階配對
            phone: rootState.donation.contact.phone,
          },
        };
        const resp = await confirmLinePayTransaction(payload);
        return resp.data;
      } catch(err) {
        console.error(err);
        return {
          'errorMessage': '沒有獲得amount和transactionId',
        };
      }
    },

    async updateRecurringDonation({ state, commit }, result) {
      const statusCode = result?.statusCode;
      const payload = {
        recurDonationId: state.oppoId,
        recurDonationRegKey: result?.response?.info?.regKey || '',
        opp3rdStatusCode: result?.statusCode || '',
        opp3rdTransactionId: state.transactionId,
        opp3rdStatusMsg: result?.errorMessage || '',
        opp3rdPaymentDate: new Date().toLocaleString('en-US', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(/\//g, '/').replace(',', ''),
      };
      const transactionResult = (statusCode == '0000') ? '捐款成功' : `付款失敗 (參考訊息:${ result?.errorMessage })`;
      commit('updateResult', transactionResult);
      try {
        // 更新 recurring donation 結果回 Salesforce
        await updateRecurringDonation(payload);
      } catch(err) {
        alert(err);
      }
    },
    async updateOpportunityOrderInfo({ state, commit }, result) {

      // 為了避免使用者 refresh 頁面，如果已經打過去後端，就不要再打一次
      if(localStorage.getItem(`${state.oppoId}_updated`) || result?.statusCode == '1172') {
        throw new Error('此交易過去已捐款成功，請勿重複刷新頁面');
      }

      try {
        const statusCode = result?.statusCode;
        const response = typeof result?.response === 'string' ? JSON.parse(result?.response || '{}') : result?.response || {};
        const now = new Date();

        const payload = {
          oppoID: state.oppoId,
          oppoOrderType: 'LINE',
          oppoCloseDate: `${ now.getFullYear() }-${ now.getMonth() + 1 }-${ now.getDate() }`,
          oppoOrderInfo: '',
          oppoECPayNo: '',
          oppLPStatusCode: statusCode,
          oppLPStatusMsg: result?.errorMessage,
          oppLPTransactionId: (statusCode == '0000') ? state.transactionId : ' ',
          oppLPMaskedCardNum: (statusCode == '0000') ? response.info.payInfo[0].maskedCreditCardNumber : ' ',
          oppLPPayMethod: (statusCode == '0000') ? response.info.payInfo[0].method : ' ',
        };

        const transactionResult = (statusCode == '0000') ? '捐款成功' : `付款失敗 (參考訊息:${ result?.errorMessage })`;
        commit('updateResult', transactionResult);

        // 表示此 oppoID 紀錄已經更新到後端
        localStorage.setItem(`${state.oppoId}_updated`, true);
        const resp = await updateOpportunityOrderInfo(payload);
        if(resp['rtnStatus'] == 'Fail') {
          throw new Error(resp.msg);
        }
        return resp;
      } catch(err) {
        console.error(err);
        alert(err);
      }
    },
  },
};
